import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, DialogBody, DialogFooter, InputGroup, Spinner } from '@blueprintjs/core';
import { toast } from 'sonner';
import { editAssignment, fetchAssignmentDetail, fetchAssignments, fetchReps, newIdempotencyKey, type AssignmentListRow, type BatchStatus, type JobState } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { Card, Empty, ErrorNote, Pill, fmtDay, fmtInt, fmtWhen, useConfirm } from './ui';
import { FileChip } from './FileChip';
import QuickAssign from './QuickAssign';
import { PickList } from '../ui/PickList';

const STATE: Record<string, { label: string; tone: string }> = {
  applied: { label: 'Active', tone: 'success' },
  partially_applied: { label: 'Partly active', tone: 'warn' },
};
const HOW: Record<string, string> = { manual: 'Picked rows', all: 'All matching', first_n: 'First N', random_n: 'Random N' };

/** Why a batch has no assignments yet, in terms of where the import is. */
function emptyText(status: BatchStatus | undefined, job: JobState | null | undefined): { title: string; text: string } {
  if (status === 'Committing') {
    return { title: 'Import still running', text: job?.waiting_for_worker
      ? 'The import is waiting for the import service to start. Leads can be given to reps once it finishes.'
      : 'Leads can be given to reps as soon as the import finishes.' };
  }
  if (status === 'Failed') return { title: 'Import stopped', text: 'Resume the import first. Leads can be given to reps once it has finished.' };
  if (status === 'Cancelled') return { title: 'Import stopped', text: 'This import was stopped. Resume it to give its leads to reps.' };
  if (status === 'Committed') return { title: 'No assignments yet', text: 'Go to the Leads tab and use "Give leads to a rep" on the right.' };
  return { title: 'No assignments yet', text: 'Use New assignment to choose leads from any import and give them to a rep.' };
}

/** Where an assignment's leads came from: one import, or several. */
function ImportCell({ a, onOpenBatch }: { a: AssignmentListRow; onOpenBatch?: (id: number) => void }) {
  const first = a.batch != null ? { id: a.batch, file_name: a.file_name, batch_date: a.batch_date } : a.imports?.[0];
  if (!first) return <span className="li-muted">Across imports</span>;
  const more = a.batch == null ? (a.imports?.length ?? 1) - 1 : 0;
  return (
    <>
      <button type="button" className="li-btn li-btn--link" title={first.file_name ?? ''} onClick={(e) => { e.stopPropagation(); onOpenBatch?.(first.id); }}>{first.file_name}</button>
      {more > 0
        ? <span className="li-subtag" title={a.imports?.slice(1).map((b) => b.file_name).join(', ')}>+{fmtInt(more)} more</span>
        : <span className="li-subtag">{fmtDay(first.batch_date)}</span>}
    </>
  );
}

/** Saved assignments of one batch (batchId) or all batches (null). */
export default function AssignmentsView({ batchId, onOpenBatch, onNew, batchStatus, batchJob, canDistribute = false }: {
  batchId: number | null; onOpenBatch?: (id: number) => void; onNew?: () => void; batchStatus?: BatchStatus; batchJob?: JobState | null;
  /** One import whose leads can still be given out: offer "Distribute {rep} additional 300" per rep (spec v4 A8). */
  canDistribute?: boolean;
}) {
  const empty = emptyText(batchStatus, batchJob);
  const q = useQuery({ queryKey: leadImportKeys.assignments(batchId), queryFn: () => fetchAssignments(batchId), ...queryPolicies.pagedData });
  const [open, setOpen] = useState<number | null>(null);
  const rows = q.data ?? [];
  const [more, setMore] = useState<{ rep: number; name: string } | null>(null);
  const [, setParams] = useSearchParams();
  /** The hub's Redistribute tab, starting from this rep's leads of this import (spec v4 A.12). */
  const redistribute = (rep: number) => setParams(() => new URLSearchParams({ tab: 'redistribute', from_rep: String(rep), from_import: String(batchId) }));
  const repsHere = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of rows) if (a.rep != null && a.rep_name && a.state !== 'cancelled') seen.set(a.rep, a.rep_name);
    return [...seen].map(([rep, name]) => ({ rep, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  return (
    <div className="li-stack">
      <Card title={batchId ? 'Assignments in this import' : 'Assignments'} flush
        actions={onNew && <Button intent="primary" icon="plus" onClick={onNew}>New assignment</Button>}>
        <ErrorNote error={q.error} />
        {q.isLoading ? <div className="li-card__body"><Spinner size={20} /></div> : rows.length === 0 ? (
          <Empty title={empty.title}>{empty.text}</Empty>
        ) : (
          <div className="li-tablewrap">
            <table className="li-table">
              <thead><tr><th>Rep</th>{!batchId && <th>Import</th>}<th className="r">Leads</th><th>How</th><th>Status</th><th className="r">Saved</th>{batchId != null && <th />}</tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} onClick={() => setOpen(a.id)} className="is-clickable">
                    <td><b>{a.rep_name ?? '-'}</b></td>
                    {!batchId && <td className="li-ellipsis"><ImportCell a={a} onOpenBatch={onOpenBatch} /></td>}
                    <td className="r num" title={a.first_revision?.requested != null ? `${fmtInt(a.first_revision.requested)} asked for` : undefined}>{fmtInt(a.effective_count)}</td>
                    <td>{HOW[a.first_revision?.mode ?? ''] ?? '-'}{a.revisions > 1 && <span className="li-subtag">edited {a.revisions - 1}x</span>}</td>
                    <td>{(() => { const st = STATE[a.state] ?? { label: a.state.charAt(0).toUpperCase() + a.state.slice(1).replace(/_/g, ' '), tone: 'muted' }; return <Pill tone={st.tone}>{st.label}</Pill>; })()}</td>
                    <td className="r num" title={a.created_by ? `by ${a.created_by}` : undefined}>{fmtWhen(a.created_at)}</td>
                    {batchId != null && (
                      <td className="r">
                        {a.rep != null && a.effective_count > 0 && (
                          <Button small icon="people" onClick={(e) => { e.stopPropagation(); redistribute(a.rep as number); }}
                            aria-label={`Redistribute ${a.rep_name}'s leads`}>Redistribute</Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {canDistribute && batchId != null && repsHere.length > 0 && (
        <Card title="Give a rep more" subtitle="The next eligible leads of this import, 300 by default.">
          <div className="li-row li-wrap" aria-label="Distribute more">
            {repsHere.map((r) => (
              <Button key={r.rep} icon="plus" onClick={() => setMore(r)}>Distribute {r.name} additional 300</Button>
            ))}
          </div>
        </Card>
      )}
      {more && batchId != null && (
        <Dialog isOpen onClose={() => setMore(null)} title={`Distribute more to ${more.name}`} style={{ width: 'min(460px, 100vw)' }}>
          <DialogBody>
            <QuickAssign batchId={batchId} fixedRep={more} onSaved={() => setMore(null)} />
          </DialogBody>
        </Dialog>
      )}
      {open != null && <AssignmentDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function AssignmentDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: leadImportKeys.assignment(id), queryFn: () => fetchAssignmentDetail(id), ...queryPolicies.recordDetail });
  const reps = useQuery({ queryKey: leadImportKeys.reps, queryFn: fetchReps, ...queryPolicies.configuration });
  const [newRep, setNewRep] = useState('');
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const a = q.data?.assignment;
  const members = useMemo<{ lead: number; phone: string; business_name: string | null; first_name: string | null; last_name: string | null; state: string | null;
                   batch: number; controlled: boolean | null; current_rep_name: string | null }[]>(() => q.data?.members ?? [], [q.data]);
  const controlled = useMemo(() => members.filter((m) => m.controlled), [members]);
  const [confirm, confirmDialog] = useConfirm();

  const run = useMutation({
    mutationFn: ({ action, payload }: { action: 'change_rep' | 'remove'; payload: Record<string, unknown> }) =>
      editAssignment(id, action, payload, a.version, newIdempotencyKey(), reason),
    onSuccess: (r) => {
      toast.success(`Saved revision. ${fmtInt(r.applied ?? 0)} leads changed.`);
      setReason(''); setChecked(new Set());
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Dialog isOpen onClose={onClose} title={a ? `Assignment #${a.id} to ${a.rep_name}` : 'Assignment'} style={{ width: 'min(860px, 100vw)' }}>
      <DialogBody>
        {q.isLoading && <Spinner size={20} />}
        <ErrorNote error={q.error} />
        {a && (
          <div className="li-stack">
            <div className="li-row li-wrap">
              {a.lead_import != null ? (
                <>
                  <FileChip batchId={a.lead_import} name={a.file_name} />
                  <span className="li-muted">batch date {fmtDay(a.batch_date)}</span>
                </>
              ) : (q.data?.imports ?? []).map((b: { id: number; file_name: string | null }) => <FileChip key={b.id} batchId={b.id} name={b.file_name} />)}
              <Pill tone="info">{fmtInt(a.effective_count)} leads held by this assignment</Pill>
              {members.length > controlled.length && <Pill tone="warn">{fmtInt(members.length - controlled.length)} moved away by later operations</Pill>}
            </div>

            <div className="li-grid2">
              <div className="li-stack">
                <span className="li-field__label">Change rep for the whole assignment</span>
                <div className="li-row">
                  <PickList value={newRep} onChange={setNewRep} ariaLabel="New rep" placeholder="Choose a rep"
                    options={(reps.data ?? []).filter((r) => r.is_active && r.id !== a.rep).map((r) => ({ value: String(r.id), label: r.name }))} />
                  <Button intent="primary" disabled={!newRep || !reason.trim()} loading={run.isPending}
                    onClick={() => run.mutate({ action: 'change_rep', payload: { rep: Number(newRep) } })}>Change rep</Button>
                </div>
              </div>
              <div className="li-stack">
                <span className="li-field__label">Remove leads</span>
                <div className="li-row">
                  <Button disabled={checked.size === 0 || !reason.trim()} loading={run.isPending}
                    onClick={() => run.mutate({ action: 'remove', payload: { lead_ids: [...checked] } })}>Remove {fmtInt(checked.size)} checked</Button>
                  <Button intent="danger" minimal disabled={controlled.length === 0 || !reason.trim()} loading={run.isPending}
                    onClick={async () => {
                      if (await confirm({ title: 'Undo this assignment?', body: `All ${fmtInt(controlled.length)} leads it still holds become unassigned.`, action: 'Undo assignment', danger: true })) {
                        run.mutate({ action: 'remove', payload: { lead_ids: controlled.map((m) => m.lead) } });
                      }
                    }}>
                    Undo assignment
                  </Button>
                </div>
              </div>
            </div>
            <InputGroup value={reason} placeholder="Reason (required for changes)" onChange={(e) => setReason(e.target.value)} />

            <div className="li-tablewrap" style={{ maxHeight: 280 }}>
              <table className="li-table li-table--compact">
                <thead><tr><th style={{ width: 30 }} /><th>Business</th><th>Name</th><th>Phone</th><th>State</th><th>Rep now</th></tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.lead} className={m.controlled ? '' : 'is-muted'}>
                      <td><Checkbox disabled={!m.controlled} checked={checked.has(m.lead)} aria-label="Select"
                        onChange={() => setChecked((cur) => { const s = new Set(cur); if (s.has(m.lead)) s.delete(m.lead); else s.add(m.lead); return s; })} /></td>
                      <td>{m.business_name}</td><td>{[m.first_name, m.last_name].filter(Boolean).join(' ')}</td><td>{m.phone}</td><td>{m.state}</td>
                      <td>{m.current_rep_name ?? <span className="li-muted">unassigned</span>}{!m.controlled && <Pill tone="muted">moved</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <span className="li-field__label">History</span>
            <ul className="li-list">
              {(q.data?.revisions ?? []).map((r: { id: number; revision_number: number; action: string; rep_name: string; by: string | null; created_at: string;
                                                   reason: string | null; actual_count: number | null; conflict_count: number; state: string; selection_mode: string | null }) => (
                <li key={r.id}>
                  <b>Revision {r.revision_number}</b>: {r.action.replace('_', ' ')}{r.selection_mode ? ` (${r.selection_mode.replace('_', ' ')})` : ''} to {r.rep_name},
                  {' '}{fmtInt(r.actual_count)} applied{r.conflict_count ? `, ${fmtInt(r.conflict_count)} conflicts` : ''}, {fmtWhen(r.created_at)}{r.by ? ` by ${r.by}` : ''}
                  {r.reason ? `. Reason: ${r.reason}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogBody>
      <DialogFooter actions={<Button onClick={onClose}>Close</Button>} />
      {confirmDialog}
    </Dialog>
  );
}
